import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export const generateCalibrationCertificatePdf = (certificates: any[]) => {
  const doc = new jsPDF({
    orientation: "p",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  certificates.forEach((certificate, index) => {
    if (index > 0) {
      doc.addPage();
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // ===========================
    // HEADER
    // ===========================
    const drawHeader = () => {
      doc.addImage("/logo.png", "PNG", 10, 8, 43, 18);

      doc.setFontSize(9);

      doc.text(
        [
          "ABN : 94063128844",
          "2/22, MICHELLAN COURT, BAYSWATER, VIC 3132",
          "Australia wide - 1300 669 996",
          "sales@universalweighing.com.au",
          "www.universalweighing.com.au",
        ],
        pageWidth - 10,
        10,
        {
          align: "right",
        },
      );

      doc.line(10, 30, pageWidth - 10, 30);

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");

      doc.text("TEST / CALIBRATION CERTIFICATE", pageWidth / 2, 40, {
        align: "center",
      });

      doc.line(45, 41, pageWidth - 45, 41);

      doc.setFont("helvetica", "normal");
    };

    const drawFooter = () => {
      const y = pageHeight - 22;

      // ===========================
      // Footer text
      // ===========================
      doc.setFontSize(8);
      doc.setTextColor(200, 0, 0);

      doc.text(
        "All tests for trade scales carried out in accordance with NMI R76 and test weights used for calibration are checked and verified under NMI Regulation 13.",
        pageWidth / 2,
        y,
        {
          align: "center",
          maxWidth: 180,
        },
      );

      // ===========================
      // Divider line
      // ===========================
      doc.setDrawColor(0, 0, 0);
      doc.line(10, y + 5, pageWidth - 10, y + 5);

      // ===========================
      // Logos
      // ===========================
      doc.addImage("/dano.png", "PNG", 10, y + 7, 20, 15);

      doc.addImage("/uwqs.jpg", "JPEG", pageWidth / 2 - 8, y + 7, 15, 15);

      doc.addImage("/wiaa.jpg", "JPEG", pageWidth - 36, y + 7, 26, 16);

      doc.setTextColor(0, 0, 0);
    };

    // Draw header
    drawHeader();

    // ===========================
    // CUSTOMER DETAILS
    // ===========================
    autoTable(doc, {
      startY: 46,

      theme: "grid",

      body: [
        [
          {
            content: "Customer Name",
            styles: {
              fontStyle: "bold",
              cellWidth: 55,
            },
          },

          certificate.customerName || "",
        ],

        [
          {
            content: "Address",
            styles: {
              fontStyle: "bold",
            },
          },

          certificate.customerAddress || "",
        ],

        [
          {
            content: "Scale Location",
            styles: {
              fontStyle: "bold",
            },
          },

          certificate.scaleLocation || "",
        ],

        [
          {
            content: "Contact Name",
            styles: {
              fontStyle: "bold",
            },
          },

          certificate.contactPerson || "",
        ],
      ],

      styles: {
        fontSize: 9,
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        cellPadding: 2,
        valign: "middle",
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    let currentY = (doc as any).lastAutoTable.finalY + 6;

    // ===========================
    // SCALE DETAILS
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      head: [
        [
          "Report No.",
          "Make & Model",
          "Capacity",
          "S/Number",
          "ID",
          "NMI Number",
        ],
      ],

      body: [
        [
          certificate.reportNo || "",
          `${certificate.make || ""} ${certificate.model || ""}`,
          certificate.capacity || "",
          certificate.serialNumber || "",
          certificate.identifier || "",
          certificate.nmiNumber || "",
        ],
      ],

      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        valign: "middle",
        halign: "center",
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 45 },
        2: { cellWidth: 30 },
        3: { cellWidth: 27 },
        4: { cellWidth: 30 },
        5: { cellWidth: 33 },
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 5;

    // ===========================
    // CALIBRATION TABLE
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      head: [
        [
          "Load Applied",
          "Before Calibration",
          "Error +/-",
          "After Calibration",
          "Error +/-",
        ],
      ],

      body: Array.from({ length: 5 }).map((_, index) => {
        const x = certificate.lineItems?.[index] || {};

        return [
          x.loadApplied || "",
          x.beforeCalibration || "",
          x.beforeCalibrationError || "",
          x.afterCalibration || "",
          x.afterCalibrationError || "",
        ];
      }),

      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        valign: "middle",
        halign: "center",
        minCellHeight: 7,
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },

      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 46 },
        2: { cellWidth: 28.9 },
        3: { cellWidth: 46 },
        4: { cellWidth: 28.9 },
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 6;

    // ===========================
    // PASS / FAIL
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      head: [["Before Calibration", "After Calibration"]],

      body: [
        [
          Number(certificate.beforeCalibration) === 0 ? "PASS" : "FAIL",

          Number(certificate.afterCalibration) === 0 ? "PASS" : "FAIL",
        ],
      ],

      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        valign: "middle",
        halign: "center",
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 6;

    // ===========================
    // COMMENTS
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      body: [
        [
          {
            content: "Comments",
            styles: {
              fontStyle: "bold",
              cellWidth: 30,
            },
          },
          {
            content: certificate.comments || "",
            styles: {
              minCellHeight: 28,
            },
          },
        ],
      ],

      styles: {
        fontSize: 9,
        cellPadding: 3,
        lineWidth: 0.2,
        lineColor: [0, 0, 0],
        valign: "middle",
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 6;

    // ===========================
    // FREQUENCY
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      head: [
        [
          "Frequency(Months)",
          "Maximum Permissible Error",
          "Verification Code",
        ],
      ],

      body: [
        [
          certificate.frequency || "",
          certificate.maximumPermissibleError || "",
          certificate.verificationCode?.trim()
            ? certificate.verificationCode
            : "N/A",
        ],
      ],

      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 6;

    // ===========================
    // SCALE CERTIFIED
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      head: [["Scale Certified", "Scale Used For Trade", "Verification Date"]],

      body: [
        [
          Number(certificate.scaleCertified) === 0 ? "YES" : "NO",

          Number(certificate.scaleUsedForTrade) === 0 ? "YES" : "NO",

          certificate.verificationDate
            ? new Date(certificate.verificationDate).toLocaleDateString("en-GB")
            : "N/A",
        ],
      ],

      styles: {
        fontSize: 8,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        halign: "center",
        valign: "middle",
      },

      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: "bold",
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    currentY = (doc as any).lastAutoTable.finalY + 6;

    // ===========================
    // FINAL ROW
    // ===========================
    autoTable(doc, {
      startY: currentY,

      theme: "grid",

      body: [
        [
          {
            content: `Calibrated By: ${certificate.calibratedBy || ""}`,
          },

          {
            content: `Date: ${
              certificate.date
                ? new Date(certificate.date).toLocaleDateString("en-GB")
                : ""
            }`,
          },

          {
            content: `Date Due: ${
              certificate.dueDate
                ? new Date(certificate.dueDate).toLocaleDateString("en-GB")
                : ""
            }`,
          },
        ],
      ],

      styles: {
        fontSize: 9,
        cellPadding: 2,
        lineWidth: 0.1,
        lineColor: [0, 0, 0],
        minCellHeight: 10,
        valign: "middle",
      },

      margin: {
        left: 10,
        right: 10,
      },
    });

    // ===========================
    // FOOTER
    // ===========================
    drawFooter();
  });

  const fileName =
    certificates.length === 1
      ? `${(certificates[0].customerName || "CalibrationCertificate")
          .replace(/[<>:"/\\|?*]+/g, "_")
          .trim()}.pdf`
      : "CalibrationCertificates.pdf";

  doc.save(fileName);
};
